/**
 * @description 구조요청 링크(/client/...)를 어떤 브라우저에서 열어야 하는지 판별하는 곳
 *
 * 알림톡 버튼은 카카오 인앱 WebView 로 열린다. 인앱 WebView 는 카메라(getUserMedia)·위치(watchPosition)
 * 권한이 앱 정책에 묶이고, 카카오톡을 벗어나면 페이지가 바로 죽어 동원시간 동안 위치를 못 보낸다.
 *
 * 정책
 *  - Android : 검증된 브라우저(크롬, 삼성인터넷)는 통과. 그 외(카카오·네이버 등 앱 내 WebView, 파이어폭스, 웨일 …)는 크롬으로 보낸다.
 *              앱 내 WebView 는 googlechrome:// 스킴 → intent:// 순으로 자동 시도한다(앱이 intent 를 직접 실행하므로 확인창이 없다).
 *              정식 브라우저는 다른 앱을 띄울 때 반드시 확인창을 띄우므로 intent 를 한 번만 시도하고 수동 버튼으로 넘긴다.
 *              삼성인터넷을 통과시키는 이유: 문자(SMS) 링크는 기본 브라우저로 열리는데, 거기서 크롬으로 강제하면
 *              삼성인터넷의 "다른 앱으로 열기" 확인창을 피할 수 없다. 삼성인터넷은 실기기 검증된 브라우저다(media-capability.ts).
 *  - iOS     : 카카오 인앱일 때만 밖으로 보낸다. 1차 googlechromes:// (크롬 설치 시) → 2차 kakaotalk://web/openExternal (Safari).
 *              (iOS 는 크롬 설치 여부를 알 수 없고, Safari/크롬 모두 WebKit 이라 WebRTC 동작은 같다)
 *  - 데스크톱 : 그대로 통과 (알림톡 대상이 아니고 개발·테스트용)
 *
 * Flashphoner 의 Browser.isChrome() 은 window.chrome 과 UA 의 "Chrome" 문자열만 보기 때문에
 * 삼성인터넷·웨일·카카오 WebView 도 크롬으로 판정한다. 여기서는 쓰지 않는다.
 */

export type RedirectPlan =
  | { type: 'none' }
  | { type: 'android'; inApp: boolean; schemeUrl: string; intentUrl: string; storeUrl: string }
  | { type: 'ios'; chromeUrl: string; externalUrl: string };

const CHROME_PACKAGE = 'com.android.chrome';
const CHROME_STORE_URL = `https://play.google.com/store/apps/details?id=${CHROME_PACKAGE}`;
const NOT_REAL_CHROME = /\bwv\b|SamsungBrowser|EdgA|OPR|Whale|Firefox|NAVER|KAKAOTALK|Instagram|FBAN|FBAV|Line\//i;
const IN_APP_WEBVIEW = /\bwv\b|KAKAOTALK|NAVER|Instagram|FBAN|FBAV|Line\//i;

const brandsOf = (): { brand: string }[] | undefined => {
  const brands = (navigator as any).userAgentData?.brands;
  return Array.isArray(brands) && brands.length > 0 ? brands : undefined;
};

export const isKakaoTalk = (ua: string): boolean => /KAKAOTALK/i.test(ua);

export const isAndroid = (ua: string): boolean => /Android/i.test(ua);

export const isIOS = (ua: string): boolean =>
  /iPhone|iPad|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export const isAndroidRealChrome = (ua: string): boolean => {
  const brands = brandsOf();
  if (brands) {
    return brands.some((item) => item.brand === 'Google Chrome');
  }
  return /Chrome\//.test(ua) && !NOT_REAL_CHROME.test(ua);
};

export const isSamsungInternet = (ua: string): boolean => {
  const brands = brandsOf();
  if (brands && brands.some((item) => item.brand === 'Samsung Internet')) {
    return true;
  }
  return /SamsungBrowser/i.test(ua);
};

export const isAndroidInAppWebView = (ua: string): boolean => {
  const brands = brandsOf();
  if (brands && brands.some((item) => item.brand === 'Android WebView')) {
    return true;
  }
  return IN_APP_WEBVIEW.test(ua);
};

const stripScheme = (href: string): string => href.replace(/^https?:\/\//i, '');

export const buildAndroidChromeSchemeUrl = (href: string): string =>
  `googlechrome://navigate?url=${encodeURIComponent(href)}`;

export const buildAndroidChromeIntentUrl = (href: string): string =>
  `intent://${stripScheme(href)}#Intent;scheme=https;package=${CHROME_PACKAGE};end`;

export const buildIosChromeUrl = (href: string): string => `googlechromes://${stripScheme(href)}`;

export const buildKakaoExternalUrl = (href: string): string =>
  `kakaotalk://web/openExternal?url=${encodeURIComponent(href)}`;

export const planExternalBrowser = (href: string, ua: string = navigator.userAgent): RedirectPlan => {
  if (isAndroid(ua)) {
    if (isAndroidRealChrome(ua) || isSamsungInternet(ua)) {
      return { type: 'none' };
    }
    return {
      type: 'android',
      inApp: isAndroidInAppWebView(ua),
      schemeUrl: buildAndroidChromeSchemeUrl(href),
      intentUrl: buildAndroidChromeIntentUrl(href),
      storeUrl: CHROME_STORE_URL,
    };
  }
  if (isIOS(ua) && isKakaoTalk(ua)) {
    return { type: 'ios', chromeUrl: buildIosChromeUrl(href), externalUrl: buildKakaoExternalUrl(href) };
  }
  return { type: 'none' };
};

/**
 * 순서대로 시도할 URL 목록.
 * 앱 내 WebView 와 iOS 카카오는 확인창이 없어 자동으로 이어서 시도해도 되지만,
 * 정식 브라우저는 시도마다 확인창이 뜨므로 한 번만 시도한다(확인창이 겹치지 않게).
 */
export const redirectAttemptsOf = (plan: RedirectPlan): string[] => {
  switch (plan.type) {
    case 'android':
      return plan.inApp ? [plan.schemeUrl, plan.intentUrl] : [plan.intentUrl];
    case 'ios':
      return [plan.chromeUrl, plan.externalUrl];
    default:
      return [];
  }
};
